import {
    type IframeHTMLAttributes,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from "react";

import { cn } from "../../lib/cn";
import { ShimmerLoadingOverlay } from "./ShimmerLoading";

type ShimmerLoadingIframeProps = IframeHTMLAttributes<HTMLIFrameElement> & {
    iframeRef?: RefObject<HTMLIFrameElement | null>;
    isReady?: boolean;
    readyMessage?: boolean;
    variant?: "default" | "light";
    wrapperClassName?: string;
};

export function ShimmerLoadingIframe({
    className,
    iframeRef,
    isReady,
    onLoad,
    readyMessage = false,
    src,
    srcDoc,
    variant = "default",
    wrapperClassName,
    ...iframeProps
}: ShimmerLoadingIframeProps) {
    const fallbackIframeRef = useRef<HTMLIFrameElement | null>(null);
    const resolvedIframeRef = iframeRef ?? fallbackIframeRef;
    const sourceKeyRef = useRef(src ?? srcDoc);
    const [loadedSourceKey, setLoadedSourceKey] = useState<string>();
    const [readySourceKey, setReadySourceKey] = useState<string>();
    const sourceKey = src ?? srcDoc;
    sourceKeyRef.current = sourceKey;

    useEffect(
        function listenForReadyMessage() {
            if (!readyMessage) {
                return;
            }

            function receiveMessage(event: MessageEvent<{ type?: string }>) {
                if (event.source !== resolvedIframeRef.current?.contentWindow) {
                    return;
                }

                if (!/(^|\.)ready$/.test(event.data?.type ?? "")) {
                    return;
                }

                setReadySourceKey(sourceKeyRef.current);
            }

            window.addEventListener("message", receiveMessage);

            return () => {
                window.removeEventListener("message", receiveMessage);
            };
        },
        [readyMessage, resolvedIframeRef],
    );

    const loadReady = !!sourceKey && loadedSourceKey === sourceKey;
    const messageReady = readyMessage && !!sourceKey && readySourceKey === sourceKey;
    const iframeIsReady = isReady ?? (readyMessage ? messageReady : loadReady);

    return (
        <ShimmerLoadingOverlay
            isReady={iframeIsReady}
            variant={variant}
            className={cn("h-full w-full", wrapperClassName)}
        >
            <iframe
                {...iframeProps}
                ref={resolvedIframeRef}
                src={src}
                srcDoc={srcDoc}
                className={cn("h-full w-full border-0", className)}
                onLoad={(event) => {
                    setLoadedSourceKey(sourceKey);
                    onLoad?.(event);
                }}
            />
        </ShimmerLoadingOverlay>
    );
}
